<script>
import { HistoryLogService } from "@/services/HistoryLogService";
import {permissionMixin} from "@/mixins/permissionMixins";
import AppLoading from "@/components/elements/AppLoading.vue";
export default {
  components: {AppLoading},
  data() {
    return {
      logs: [],
      currentPage: 1,
      lastPage: 1,
      perPage: 30,
      password: '',
      paginationWindow: 10,
      loading: false,
    };
  },
  computed: {
    paginatedPages() {
      const pages = [];
      let start = Math.max(1, this.currentPage - Math.floor(this.paginationWindow / 2));
      let end = start + this.paginationWindow - 1;
      if (end > this.lastPage) {
        end = this.lastPage;
        start = Math.max(1, end - this.paginationWindow + 1);
      }
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      return pages;
    }
  },
  mixins: [permissionMixin],
  methods: {
    formatAction(action) {
      const map = {
        create: 'NEWLY CREATED',
        update: 'UPDATED',
        delete: 'DELETED'
      }
      return map[action] ?? action
    },
    async fetchLogs(page = 1) {
      this.loading = true;
      try {
        const res = await HistoryLogService.getAllManagingVesselHistoryLogs(page, this.perPage);
        if (!res.error) {
          this.logs = res.data;
          this.currentPage = res.current_page;
          this.lastPage = res.last_page;
          this.perPage = res.per_page;
          this.from = res.from;
        }
      } catch (err) {
      } finally {
        this.loading = false;
      }
    },
    goToPage(page) {
      if (page >= 1 && page <= this.lastPage) {
        this.fetchLogs(page);
      }
    },
    formatJson(obj) {
      if (!obj || (typeof obj === "object" && Object.keys(obj).length === 0)) {
        return [];
      }

      const fieldMap = {
        year_event: "Year Event",
        total_number_vessel_average: "Total Number Vessel Average",
        kobe_total: "Kobe",
        stgt_ogd_total: "STGT OGD",
        stargate_bulgaria_total: "Stargate Bulgaria",
        stgt_eccd_total: "STGT ECCD",
        rohq_total: "ROHQ"
      };

      return Object.entries(fieldMap)
        .filter(([key]) => obj.hasOwnProperty(key))
        .map(([key, label]) => ({
          label,
          value: obj[key]
        }));
    },
    formatTimestamp(ts) {
      if (!ts) return { date: "-", time: "-" };
      const date = new Date(ts);

      const optionsDate = {
        year: "numeric",
        month: "long",
        day: "numeric",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      const optionsTime = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      };

      return {
        date: new Intl.DateTimeFormat("en-US", optionsDate).format(date),
        time: new Intl.DateTimeFormat("en-US", optionsTime).format(date)
      };
    },
  },
  mounted() {
    this.fetchLogs(this.currentPage);
  }
};
</script>

<template>
  <div>
    <div class="row align-items-center mb-2">
      <div class="col-sm-6">
        <h3 class="text-left">History Logs of Managing Vessel</h3>
      </div>
    </div>

    <div class="row align-items-center mb-3">
      <div class="col-sm-6">
        <nav aria-label="Page navigation">
          <ul class="pagination justify-content-start mb-0">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
              <button class="page-link" @click="goToPage(1)">First</button>
            </li>
            <li
              class="page-item"
              v-for="page in paginatedPages"
              :key="page"
              :class="{ active: page === currentPage }"
            >
              <button class="page-link" @click="goToPage(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === lastPage }">
              <button class="page-link" @click="goToPage(lastPage)">Last</button>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <div class="col-12 pl-0 text-center col-lg-12">
      <div class="row no-gutters">
        <table class="table table-sm table-bordered small mt-2">
          <thead>
          <tr>
            <th class="text-center text-truncate" style="max-width: 50px;">#</th>
            <th class="text-center text-truncate" style="max-width: 120px;">Year Event</th>
            <th class="text-center text-truncate" style="max-width: 150px;">Old Value</th>
            <th class="text-center text-truncate" style="max-width: 150px;">New Value</th>
            <th class="text-center text-truncate" style="max-width: 120px;">Timestamp</th>
            <th class="text-center text-truncate" style="max-width: 120px;">User</th>
            <th class="text-center text-truncate" style="max-width: 150px;">Activity Type</th>
            <th class="text-center text-truncate" style="max-width: 150px;">Role</th>
          </tr>
          </thead>
          <tbody>
          <tr v-if="loading">
            <td colspan="100%">
              <app-loading></app-loading>
            </td>
          </tr>
          <tr v-else-if="logs.length === 0">
            <td colspan="7" class="text-center">No logs found</td>
          </tr>
          <tr v-else v-for="(log, index) in logs" :key="log.id">
            <td class="text-center align-middle font-weight-bold">{{ from + index }}</td>
            <td class="text-center align-middle">{{ log.model?.year_event }}</td>
            <td class="text-left align-middle pl-5" :style="!formatJson(log.old).length ? 'background-color: #c0c0c0;' : ''">
              <div v-if="formatJson(log.old).length">
                <div v-for="item in formatJson(log.old)" :key="item.label">
                  <span class="font-weight-bold">{{ item.label }}: </span><span>{{ item.value }}</span>
                </div>
              </div>
              <div v-else></div>
            </td>
            <td class="text-left align-middle pl-5" :style="!formatJson(log.new).length ? 'background-color: #c0c0c0;' : ''">
              <div v-if="formatJson(log.new).length">
                <div v-for="item in formatJson(log.new)" :key="item.label">
                  <span class="font-weight-bold">{{ item.label }}: </span><span class="text-danger">{{ item.value }}</span>
                </div>
              </div>
              <div v-else></div>
            </td>
            <td class="text-center align-middle ">
              <div>
                {{ formatTimestamp(log.timestamp).date }} <br>
                {{ formatTimestamp(log.timestamp).time }}
              </div>
            </td>
            <td class="text-center align-middle text-primary">{{ log.user?.name || '-' }}</td>
            <td class="text-center align-middle">
            <span
              class="text-uppercase"
              :class="{
                'badge badge-success': log.action === 'create',
                'badge badge-warning': log.action === 'update',
                'badge badge-danger': log.action === 'delete'
              }"
            >
              {{ formatAction(log.action) }}
            </span>
            </td>
            <td class="text-center align-middle" :style="'background-color: ' + getUserRole(user, true) + '; font-weight: bold'">{{ getUserRole(user) }}</td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
